import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clickEndsPress, isPlainMousePress } from '../hooks/usePressAction'; // [FLUID-8 2026-10-12]
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import { useRealtime } from '../hooks/useRealtime';
import { useIsMobile } from '../hooks/useIsMobile'; // [PHONE-LAYOUT 2026-10-24] the phone board

/**
 * [SALES-ROW 2026-09-30, user] The Team ROW Sales board — opened from the header's "ROW Dashboard"
 * button — admins, Team ROW - Sales and Vishal Suryavanshi since [ROW-ACCESS-TEAM 2026-10-01] (see SALES_ROW in
 * config/sections; the rule is server/src/services/salesRowAccess.ts).
 *
 * [ROW-BOARD 2026-09-30, user] "The Summit" — option 1 of the theme preview the user picked
 * (claude.ai artifact AQMjfyXys2Y4Svekc9KQX1), built as it looks there. Every rep climbs one mountain;
 * the higher the score, the higher they stand; the two flags at the top are the two leader seats.
 * Every figure comes from /api/sales-row/board (services/salesRowBoard.ts holds ALL the rules — this
 * page does no money). One look in both app themes, like the PM Singapore board.
 *
 * [ROW-FULLSCREEN 2026-09-30, user] "it will cover the complete page": PORTALED to document.body — a
 * plain `position: fixed` would resolve against the page card, because AppLayout's PageFade keeps a
 * transform after its entrance animation. z-index 950 sits above the dock (850) and the Go-to-top pill
 * (900) but below the drawers (999), dialogs (1000+), tooltips and toasts. Back (or Esc) returns to the
 * page the button was clicked on.
 *
 * The scene is drawn on a fixed 1600x900 stage scaled to fit the window (letterboxed, never cropped).
 * The sky fills the whole window and the foothills run the full width of the stage, so the band above
 * or below the stage on a 16:10 laptop continues the scene instead of showing an edge.
 *
 * [ROW-LIVE-FX 2026-09-30, user] "go for all the 5 things" from the live-animation preview (claude.ai
 * artifact 86AuB58LhSeHFdzhMPggS5) — the board keeps moving for as long as it is open:
 *   1 drifting mist (two SVG bands, behind the peak and low in front) · 2 starry sky (twinkling stars and
 *   a shooting star every 24 s) · 3 sunrise glow (the sun breathes, soft rays turn behind the peak) ·
 *   4 leaders' shine (seat flags wave, a gold sheen sweeps the seat cards and the top two rows, the
 *   leaders' markers pulse) · 5 climber's lantern (a light climbs the trail, SVG animateMotion).
 * Only transform and opacity move, so it can run all day on a TV. prefers-reduced-motion stops all five.
 * The stars and mist also fill the side bands of a wide window, like the landscape does.
 *
 * [ROW-SCORECARD 2026-09-30, user] A SECOND PAGE, "The Scorecard" (claude.ai artifact WWoAXCd6BmJE52uJxuy6gs,
 * "make the page to the final page now"): how every score is made — own recurring (100%) + one-time (25%)
 * − the half shared with a second voice + the half earned as one = score — plus every counted sale in a
 * list that scrolls by itself, and the score by month. Same sky, stars, header and cards as The Summit.
 * [ROW-ROTATE 2026-09-30, user] The two pages take turns every 30 s (every minute until [ROW-TURN-30S]).
 * Back · Main screen · Table sit in the top-left corner; clicking one shows that page and pauses the turns.
 * [ROW-HOLD-30S 2026-09-30, user] "Paused · auto again in 1:00 — make this to 30 seconds", then
 * [ROW-IDLE-10S 2026-09-30, user] "if the mouse is moving then it will be paused but when the mouse become
 * idle for 10 seconds then start the auto mode for the page": ANY real activity on the board (a mouse that
 * moves, a click, a key, the wheel, a touch) pauses the turns, and HOLD_MS (10 s) with none starts them
 * again; while they run, each page shows for PAGE_MS (30 s).
 * [ROW-LIST-HOVER 2026-09-30, user] "when my cursor is on this table then the flow stops … at the time i
 * remove the cursor form it then it should start its auto mode again immediately": the sales list stands
 * still while a mouse cursor is on its card and glides on the moment the cursor leaves (touch / keys: 10 s).
 */

interface BoardRep {
  name: string;
  score: number;
  recurring: number;
  oneTime: number;
  secondVoice: number;
  sales: number;
  months: Record<string, number>;
  /* [ROW-SCORECARD] the score as a sum: ownRecurring + oneTimeValue − sharedOut + secondVoice */
  ownRecurring: number;
  recurringSales: number;
  oneTimeSold: number;
  oneTimeValue: number;
  oneTimeSales: number;
  sharedOut: number;
  sharedSales: number;
  sharedTo: string[];
  earnedSales: number;
  earnedFrom: string[];
}
interface BoardSale {
  date: string | null;
  owner: string;
  website: string | null;
  kind: 'recurring' | 'oneTime';
  saleAmount: number;
  value: number;
  secondVoices: string[];
}
interface Board { start: string; end: string; generatedAt: string; reps: BoardRep[]; sales: BoardSale[] }

/** Straight through fetch, NOT the shared axios client: its five-minute stale-while-revalidate cache
 *  is right for tab switching and wrong for a live board (same reason as PMSingaporePage). */
async function getJson<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: 'include', headers: { 'Cache-Control': 'no-store' } });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

const STAGE_W = 1600;
const STAGE_H = 900;
const LAND_TOP = 180; // the mountain drawing starts this far down the stage
const GOLD = '#f4b860';
const ROSE = '#ef8f73';
const BLUE = '#9fb4ff';
const SNOW = '#eef2fb';
const INK = '#0d1224';
const DIM = '#aab5dd';
const SOFT = '#c9d0ec';
const GROUND = '#101630';
const DISPLAY = "'Big Shoulders Display', 'Arial Narrow', sans-serif";
const FADED = '#6f7aa6';
/* [ROW-TURN-30S 2026-09-30, user] "make the auto change page time to 30 seconds, not 1 minute" — each page
   shows for 30 s while the turns run by themselves */
const PAGE_MS = 30_000;
/* [ROW-IDLE-10S] the turns pause on any activity and start again after 10 s without any (was 30 s, first 1 min) */
const HOLD_MS = 10_000;
/* [ROW-SCORECARD] the sales list: speed of its own scroll, and how long it waits after a touch or a key */
const LIST_SPEED = 14; // px per second — about one row every two seconds
const LIST_IDLE_MS = 10_000;

/* The climbing route, in the mountain drawing's own coordinates (base camp → summit). */
const ROUTE: [number, number][] = [[200, 690], [470, 610], [420, 500], [560, 400], [520, 300], [610, 190], [600, 84]];
const SEGS = (() => {
  const out: { a: [number, number]; b: [number, number]; len: number; start: number }[] = [];
  let total = 0;
  for (let i = 1; i < ROUTE.length; i++) {
    const a = ROUTE[i - 1], b = ROUTE[i];
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
    out.push({ a, b, len, start: total });
    total += len;
  }
  return { list: out, total };
})();
/** Stage position `t` of the way up the route (0 = base camp, 1 = summit). */
function onRoute(t: number): [number, number] {
  const d = Math.max(0, Math.min(1, t)) * SEGS.total;
  const s = SEGS.list.find((x) => d <= x.start + x.len) || SEGS.list[SEGS.list.length - 1];
  const f = s.len ? (d - s.start) / s.len : 0;
  return [s.a[0] + (s.b[0] - s.a[0]) * f, LAND_TOP + s.a[1] + (s.b[1] - s.a[1]) * f];
}

const money = (n: number, dp = 2) => '$' + n.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });
const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');
const firstName = (name: string) => name.split(/\s+/)[0] || name;
const dayLabel = (iso: string, withYear: boolean) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', withYear ? { month: 'short', day: 'numeric', year: 'numeric' } : { month: 'short', day: 'numeric' });
};
const daysLeft = (endIso: string) => {
  const [y, m, d] = endIso.split('-').map(Number);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((new Date(y, m - 1, d).getTime() - today.getTime()) / 86400000));
};
const reduceMotion = () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
/** 'YYYY-MM-DD' → 'Sep 29'. */
const shortDay = (iso: string | null) => (iso ? dayLabel(iso, false) : '');
/** First names joined for a caption: "Hamza", "Hamza & Bhanu". */
const firsts = (names: string[]) => names.map(firstName).join(' & ');
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
/** The months the challenge covers, 'YYYY-MM', from its first to its last. */
function monthsBetween(startIso: string, endIso: string): string[] {
  const out: string[] = [];
  let [y, m] = startIso.split('-').map(Number);
  const [ey, em] = endIso.split('-').map(Number);
  while (y < ey || (y === ey && m <= em)) {
    out.push(`${y}-${String(m).padStart(2, '0')}`);
    m += 1;
    if (m > 12) { m = 1; y += 1; }
  }
  return out;
}
const monthShort = (ym: string) => new Date(Number(ym.slice(0, 4)), Number(ym.slice(5, 7)) - 1, 1).toLocaleDateString('en-US', { month: 'short' });

/* ───────────── [ROW-LIVE-FX 2026-09-30] the five always-on animations ───────────── */

const mistDrift = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-1600px); }
`;
const twinkle = keyframes`
  from { opacity: 0.1; }
  to { opacity: 0.85; }
`;
const shootingStar = keyframes`
  0% { transform: translate(0, 0) rotate(-24deg); opacity: 0; }
  1% { opacity: 1; }
  4.5% { transform: translate(-460px, 205px) rotate(-24deg); opacity: 0; }
  100% { transform: translate(-460px, 205px) rotate(-24deg); opacity: 0; }
`;
const raysSpin = keyframes`
  to { transform: rotate(360deg); }
`;
const breathe = keyframes`
  0%, 100% { transform: scale(0.94); opacity: 0.8; }
  50% { transform: scale(1.08); opacity: 1; }
`;
const flagWave = keyframes`
  from { transform: skewY(-9deg) scaleX(0.9); }
  to { transform: skewY(7deg) scaleX(1.04); }
`;
const goldSheen = keyframes`
  0% { transform: translateX(-120%); }
  20% { transform: translateX(120%); }
  100% { transform: translateX(120%); }
`;
const leaderHalo = keyframes`
  from { transform: scale(1); opacity: 0.9; }
  to { transform: scale(1.9); opacity: 0; }
`;
/* [ROW-SCORECARD] the Scorecard's rows slide in each time the page comes up */
const rowIn = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: none; }
`;

/** A seeded random, so the stars sit in the same places on every load and every re-render. */
function seeded(seed: number) {
  let s = seed;
  return () => { s = (s * 16807) % 2147483647; return s / 2147483647; };
}
/** The whole star field, over the stage AND both side bands a wide window can show (x -1600..3200),
 *  denser near the top of the sky. Only the stars inside the visible range are rendered. */
const STAR_FIELD = (() => {
  const rnd = seeded(11);
  return Array.from({ length: 360 }, () => ({
    x: -1600 + rnd() * 4800,
    y: rnd() * rnd() * 400,
    size: rnd() < 0.14 ? 3 : 2,
    dur: 2.4 + rnd() * 4,
    delay: rnd() * 6,
  }));
})();

/** One drifting mist band: soft ellipses repeated every 1600 px (four copies, so the loop is seamless
 *  across the stage and both side bands). The band drifts one copy-width per loop. */
function mistBand(y0: number, count: number, rx: number, ry: number, opacity: number, dur: number, fillId = 'rowMistG') {
  const clouds: React.ReactNode[] = [];
  for (let copy = -1; copy <= 2; copy++) {
    for (let c = 0; c < count; c++) {
      clouds.push(
        <ellipse key={`${copy}-${c}`} cx={Math.round(copy * 1600 + (c + 0.5) * (1600 / count) + (c % 2 ? 70 : -60))}
          cy={y0 + (c % 3) * 14 - 10} rx={rx + (c % 2) * 60} ry={ry + (c % 3) * 8} fill={`url(#${fillId})`} opacity={opacity} />,
      );
    }
  }
  return <g className="mist-band" style={{ ['--dur' as any]: `${dur}s` }}>{clouds}</g>;
}

/** The climber's lantern: a warm light and a three-dot trail travelling the route, faded in at base
 *  camp and out at the summit, every 14 s. SVG animateMotion — no script, no layout work. */
const LANTERN_PATH = 'M' + ROUTE.map((p) => p.join(',')).join(' L');
const LANTERN_PARTS: { r: number; fill: string; peak: number; begin: number }[] = [
  { r: 22, fill: 'url(#rowLanternG)', peak: 1, begin: 0 },
  { r: 7, fill: '#f4b860', peak: 0.55, begin: 0.28 },
  { r: 5, fill: '#f4b860', peak: 0.35, begin: 0.56 },
  { r: 3.5, fill: '#f4b860', peak: 0.2, begin: 0.84 },
];

/* ───────────── styles (one fixed look, both app themes) ───────────── */

const Screen = styled.div`
  position: fixed;
  inset: 0;
  z-index: 950;
  overflow: hidden;
  background: linear-gradient(180deg, #111934 0%, #27305f 46%, #7c6a8a 74%, #e39c7d 100%);
  color: #f3f5fb;
  font-family: 'Plus Jakarta Sans', system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif;
`;

const GroundBand = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${GROUND};
`;

const Stage = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  width: ${STAGE_W}px;
  height: ${STAGE_H}px;
  transform-origin: 50% 50%;
`;

const Sun = styled.div`
  position: absolute;
  left: 470px;
  top: 170px;
  width: 380px;
  height: 380px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 196, 140, 0.55) 0%, rgba(255, 170, 120, 0.18) 45%, rgba(255, 170, 120, 0) 70%);
  animation: ${breathe} 9s ease-in-out infinite; /* [ROW-LIVE-FX] 3 · the sun breathes */

  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

/* [ROW-LIVE-FX] 3 · soft rays turning slowly behind the peak (centred on the summit at 600,250). */
const Rays = styled.div`
  position: absolute;
  left: 100px;
  top: -250px;
  width: 1000px;
  height: 1000px;
  border-radius: 50%;
  pointer-events: none;
  background: repeating-conic-gradient(from 0deg, rgba(255, 214, 160, 0.085) 0deg 4deg, rgba(255, 214, 160, 0) 4deg 15deg);
  -webkit-mask-image: radial-gradient(circle, #000 6%, rgba(0, 0, 0, 0.4) 22%, transparent 46%);
  mask-image: radial-gradient(circle, #000 6%, rgba(0, 0, 0, 0.4) 22%, transparent 46%);
  animation: ${raysSpin} 160s linear infinite;

  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

/* [ROW-LIVE-FX] 2 · twinkling stars in the dark upper sky and a shooting star every 24 s. */
const Stars = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 0;
  height: 0;
  pointer-events: none;

  i { position: absolute; border-radius: 50%; background: #ffffff; opacity: 0.15; animation: ${twinkle} 4s ease-in-out infinite alternate; }
  .shoot {
    position: absolute;
    left: 1230px;
    top: 70px;
    width: 170px;
    height: 2px;
    border-radius: 2px;
    opacity: 0;
    background: linear-gradient(90deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0));
    animation: ${shootingStar} 24s linear 3s infinite;
  }

  @media (prefers-reduced-motion: reduce) { i, .shoot { animation: none; } }
`;

/* [ROW-FULLWIDTH 2026-09-30, user] "it is leaving some area on the right and left, cover that area too":
   on a window wider than 16:9 the stage is letterboxed at the sides, so the landscape is drawn LAND_BLEED
   past both stage edges (the stage does not clip; the Screen does). The side bands then show more ridge
   on the left and more foothills on the right instead of a hard edge. Stage coordinates are unchanged. */
const LAND_BLEED = 1600;
const Land = styled.svg`
  position: absolute;
  left: -${LAND_BLEED}px;
  top: ${LAND_TOP}px;
  width: ${STAGE_W + 2 * LAND_BLEED}px;
  height: ${STAGE_H - LAND_TOP}px;

  /* [ROW-LIVE-FX] 1 · the mist bands drift one copy-width per loop (each band sets its own --dur). */
  .mist-band { animation: ${mistDrift} var(--dur, 120s) linear infinite; }

  @media (prefers-reduced-motion: reduce) { .mist-band { animation: none; } }
`;

/* [ROW-BACK-CORNER 2026-09-30, user] "back button will be on the top left side corner" — pinned to the
   WINDOW's top-left corner (window pixels, not the scaled stage), so on any window shape it sits in the
   corner. The stage header starts low enough (Head top 96) that the two never meet.
   [ROW-ROTATE 2026-09-30, user] "beside the back button add 2 more button, one for the main screen and the
   other for the table" — the page being shown is gold.
   [ROW-NO-PILL 2026-10-01, user] The status pill after them ("Auto · next page in …" / "Paused · auto again
   in …") is gone: "remove this, no need to show this on the sales row dashboard".
   [ROW-SLIM-NAV 2026-10-01, user] "make the size … small vertically, so that it should look slim" — the
   buttons are 30 px tall (were 40); width and text unchanged. */
const Nav = styled.nav`
  position: absolute;
  z-index: 3;
  left: calc(16px + env(safe-area-inset-left, 0px));
  right: calc(16px + env(safe-area-inset-right, 0px));
  top: calc(16px + env(safe-area-inset-top, 0px));
  display: flex;
  flex-wrap: wrap; /* a phone-width window: a button drops to a second line instead of running off */
  align-items: center;
  gap: 8px;
`;

const NavButton = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 30px;
  padding: 0 16px;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$active ? GOLD : 'rgba(170, 181, 221, 0.35)')};
  background: ${(p) => (p.$active ? GOLD : 'rgba(13, 18, 36, 0.62)')};
  color: ${(p) => (p.$active ? '#1a1408' : '#f3f5fb')};
  font: 700 15px 'Plus Jakarta Sans', system-ui, sans-serif;
  cursor: pointer;
  transition: background 160ms cubic-bezier(0.4, 0, 0.2, 1), border-color 160ms cubic-bezier(0.4, 0, 0.2, 1), color 160ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover { border-color: ${GOLD}; }
  &:focus-visible { outline: 2px solid ${GOLD}; outline-offset: 3px; }
`;

/* [ROW-ROTATE] One of the two pages. They crossfade; the hidden one is fully paused, so only the page on
   screen spends any work on animation. */
const Layer = styled.div<{ $on: boolean }>`
  position: absolute;
  inset: 0;
  opacity: ${(p) => (p.$on ? 1 : 0)};
  pointer-events: ${(p) => (p.$on ? 'auto' : 'none')};
  transition: opacity 700ms ease;

  ${(p) => !p.$on && css`
    *, *::after { animation-play-state: paused !important; }
  `}

  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

const Head = styled.header`
  position: absolute;
  left: 56px;
  top: 96px;
`;

const Eyebrow = styled.div`
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${DIM};
`;

const Title = styled.h1`
  margin: 2px 0 0;
  font: 900 104px/0.92 ${DISPLAY};
  letter-spacing: 0.01em;
  text-transform: uppercase;
`;

const Dates = styled.div`
  margin-top: 10px;
  font-size: 19px;
  color: ${SOFT};
`;

const Countdown = styled.div`
  position: absolute;
  left: 700px;
  top: 52px;
  display: flex;
  align-items: baseline;
  gap: 12px;

  b { font: 900 76px/1 ${DISPLAY}; color: ${GOLD}; }
  span { font-size: 17px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: ${SOFT}; line-height: 1.25; }
`;

const Seats = styled.div`
  position: absolute;
  left: 648px;
  top: 196px;
  display: grid;
  gap: 8px;

  /* [ROW-LIVE-FX] 4 · the seat flags wave from the pole. */
  .pennant { transform-box: fill-box; transform-origin: 0% 50%; animation: ${flagWave} 2.2s ease-in-out infinite alternate; }

  @media (prefers-reduced-motion: reduce) { .pennant { animation: none; } }
`;

const Seat = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 14px 7px 10px;
  border-radius: 10px;
  background: rgba(13, 18, 36, 0.62);
  border: 1px solid rgba(244, 184, 96, 0.55);
  position: relative;
  overflow: hidden;

  small { display: block; font-size: 13px; font-weight: 800; letter-spacing: 0.16em; color: ${GOLD}; text-transform: uppercase; }
  strong { display: block; font-size: 19px; font-weight: 700; white-space: nowrap; }

  /* [ROW-LIVE-FX] 4 · a gold sheen sweeps the card every 7 s (the second card a beat later). */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    transform: translateX(-120%);
    background: linear-gradient(105deg, transparent 35%, rgba(255, 226, 170, 0.3) 50%, transparent 65%);
    animation: ${goldSheen} 7s ease-in-out infinite;
  }
  & + &::after { animation-delay: 0.35s; }

  @media (prefers-reduced-motion: reduce) { &::after { animation: none; display: none; } }
`;

/* [ROW-4K 2026-10-01, user] "make the dashboard to work in 4k so that it should look ultra clear": no
   will-change here. With it the browser keeps each marker as a picture drawn at the window size it opened
   in, so a board opened small and then made 4K (F11 on the TV) showed blurry initials and names, measured
   in headless Chrome. Without it the five markers are simply redrawn sharp at whatever size the board is. */
const Marker = styled.div<{ $lead: boolean }>`
  position: absolute;
  left: 0;
  top: 0;

  .dot {
    position: absolute;
    left: -21px;
    top: -21px;
    width: 42px;
    height: 42px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: ${INK};
    border: 3px solid ${(p) => (p.$lead ? GOLD : SNOW)};
    color: ${(p) => (p.$lead ? GOLD : '#f3f5fb')};
    box-shadow: ${(p) => (p.$lead ? '0 0 0 6px rgba(244, 184, 96, 0.22)' : 'none')};
    font: 800 19px/1 ${DISPLAY};
    letter-spacing: 0.03em;
  }
  .tag {
    position: absolute;
    left: 30px;
    top: -15px;
    white-space: nowrap;
    padding: 4px 11px;
    border-radius: 8px;
    background: rgba(13, 18, 36, 0.72);
    font-size: 16px;
    font-weight: 700;
  }
  .tag em { font-style: normal; color: ${GOLD}; margin-left: 6px; }

  /* [ROW-LIVE-FX] 4 · a soft gold ring pulses out of the two leaders' markers. */
  ${(p) => p.$lead && css`
    .dot::after {
      content: '';
      position: absolute;
      inset: -4px;
      border-radius: 50%;
      border: 2px solid rgba(244, 184, 96, 0.85);
      animation: ${leaderHalo} 2.6s ease-out infinite;
    }
    @media (prefers-reduced-motion: reduce) { .dot::after { animation: none; display: none; } }
  `}
`;

const Panel = styled.aside`
  position: absolute;
  left: 1040px;
  top: 150px;
  width: 520px;
  display: grid;
  gap: 12px;

  h2 { margin: 0; font-size: 15px; font-weight: 800; letter-spacing: 0.2em; text-transform: uppercase; color: ${DIM}; }
`;

const List = styled.ol<{ $dense: boolean }>`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: ${(p) => (p.$dense ? 7 : 10)}px;
`;

const Row = styled.li<{ $lead: boolean; $dense: boolean }>`
  display: grid;
  grid-template-columns: 46px minmax(0, 1fr) auto;
  grid-template-rows: auto auto auto;
  column-gap: 14px;
  row-gap: ${(p) => (p.$dense ? 3 : 5)}px;
  align-items: center;
  padding: ${(p) => (p.$dense ? '8px 16px' : '12px 16px')};
  border-radius: 14px;
  background: ${(p) => (p.$lead ? 'rgba(40, 32, 30, 0.55)' : 'rgba(13, 18, 36, 0.55)')};
  border: 1px solid ${(p) => (p.$lead ? 'rgba(244, 184, 96, 0.6)' : 'rgba(170, 181, 221, 0.18)')};
  position: relative;
  overflow: hidden;

  /* [ROW-LIVE-FX] 4 · the top two rows share the seat cards' gold sheen, a beat after them. */
  ${(p) => p.$lead && css`
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      pointer-events: none;
      transform: translateX(-120%);
      background: linear-gradient(105deg, transparent 35%, rgba(255, 226, 170, 0.22) 50%, transparent 65%);
      animation: ${goldSheen} 7s ease-in-out 0.7s infinite;
    }
    &:nth-child(2)::after { animation-delay: 1.05s; }
    @media (prefers-reduced-motion: reduce) { &::after { animation: none; display: none; } }
  `}

  .rank { grid-row: 1 / 3; font: 900 46px/1 ${DISPLAY}; color: ${(p) => (p.$lead ? GOLD : DIM)}; }
  .name { grid-column: 2; font-size: 20px; font-weight: 700; display: flex; align-items: center; gap: 10px; min-width: 0; }
  .name span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .name i { flex: none; font-style: normal; font-size: 11px; font-weight: 800; letter-spacing: 0.16em; padding: 3px 8px; border-radius: 6px; background: ${GOLD}; color: #1a1408; }
  .score { grid-column: 3; grid-row: 1 / 3; text-align: right; }
  .score b { display: block; font: 800 40px/1 ${DISPLAY}; font-variant-numeric: tabular-nums; }
  .score small { display: block; margin-top: 4px; font-size: 13px; font-weight: 700; color: ${GOLD}; white-space: nowrap; }
  .bar { grid-column: 2; display: flex; height: 8px; border-radius: 4px; background: rgba(170, 181, 221, 0.16); overflow: hidden; }
  .bar i { display: block; height: 100%; }
  .sub { grid-column: 2 / 4; font-size: 13px; color: #b9c1e2; font-variant-numeric: tabular-nums; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
`;

const Legend = styled.div`
  display: flex;
  gap: 18px;
  font-size: 13.5px;
  color: ${SOFT};

  span { display: inline-flex; align-items: center; gap: 7px; }
  b { width: 12px; height: 12px; border-radius: 3px; display: inline-block; }
`;

const Note = styled.p`
  margin: 0;
  padding: 18px 16px;
  border-radius: 14px;
  background: rgba(13, 18, 36, 0.55);
  font-size: 16px;
  color: ${SOFT};
`;

/* ───────────── [ROW-SCORECARD] page 2 · The Scorecard ───────────── */

/* A lower echo of The Summit's landscape along the bottom, with its own drifting mist; like the Summit's
   land it runs past both stage edges so a wide window has no hard edge. */
const Hills = styled.svg`
  position: absolute;
  left: -${LAND_BLEED}px;
  bottom: 0;
  width: ${STAGE_W + 2 * LAND_BLEED}px;
  height: 150px;

  .mist-band { animation: ${mistDrift} var(--dur, 120s) linear infinite; }

  @media (prefers-reduced-motion: reduce) { .mist-band { animation: none; } }
`;

const Formula = styled.div`
  position: absolute;
  left: 1080px;
  top: 118px;
  width: 464px;
  display: grid;
  gap: 6px;
  font-size: 15px;
  line-height: 1.55;
  color: ${SOFT};

  h2 { margin: 0; font-size: 15px; font-weight: 800; letter-spacing: 0.2em; text-transform: uppercase; color: ${DIM}; }
  .sum { display: flex; flex-wrap: wrap; gap: 6px 8px; align-items: center; font-weight: 700; color: #f3f5fb; }
  .sum i { font-style: normal; font: 800 22px/1 ${DISPLAY}; color: ${DIM}; }
`;

/* rank · rep · recurring · + · one-time · − · shared · + · earned · = · score */
const SC_COLUMNS = '56px 250px 200px 28px 220px 28px 220px 28px 220px 28px minmax(0, 1fr)';

const ScTable = styled.section<{ $dense: boolean }>`
  position: absolute;
  left: 56px;
  top: 270px;
  width: 1488px;
  display: grid;
  gap: 6px;

  .rows { display: grid; gap: ${(p) => (p.$dense ? 4 : 6)}px; }
`;

const ScHead = styled.div`
  display: grid;
  grid-template-columns: ${SC_COLUMNS};
  column-gap: 6px;
  align-items: center;
  padding: 0 16px 2px;
  font-size: 12.5px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${DIM};

  span { display: inline-flex; align-items: center; gap: 7px; }
  b { width: 10px; height: 10px; border-radius: 3px; display: inline-block; flex: none; }
  .right { justify-content: flex-end; }
`;

/* $dense: more than five reps — slimmer rows, so the table still ends above the two cards (like the Summit's list) */
const ScRow = styled.div<{ $lead: boolean; $i: number; $dense: boolean }>`
  display: grid;
  grid-template-columns: ${SC_COLUMNS};
  column-gap: 6px;
  align-items: center;
  padding: ${(p) => (p.$dense ? '3px 16px' : '6px 16px')};
  border-radius: 14px;
  background: ${(p) => (p.$lead ? 'rgba(40, 32, 30, 0.55)' : 'rgba(13, 18, 36, 0.55)')};
  border: 1px solid ${(p) => (p.$lead ? 'rgba(244, 184, 96, 0.6)' : 'rgba(170, 181, 221, 0.18)')};
  position: relative;
  overflow: hidden;
  animation: ${rowIn} 600ms cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: ${(p) => p.$i * 70}ms;

  /* the same gold sheen as The Summit's leader rows */
  ${(p) => p.$lead && css`
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      pointer-events: none;
      transform: translateX(-120%);
      background: linear-gradient(105deg, transparent 35%, rgba(255, 226, 170, 0.22) 50%, transparent 65%);
      animation: ${goldSheen} 7s ease-in-out ${0.7 + p.$i * 0.35}s infinite;
    }
  `}

  .rank { font: 900 ${(p) => (p.$dense ? 32 : 38)}px/1 ${DISPLAY}; color: ${(p) => (p.$lead ? GOLD : DIM)}; }
  .rep { font-size: 20px; font-weight: 700; display: flex; align-items: center; gap: 10px; min-width: 0; white-space: nowrap; }
  .rep span { overflow: hidden; text-overflow: ellipsis; }
  .rep i { flex: none; font-style: normal; font-size: 11px; font-weight: 800; letter-spacing: 0.16em; padding: 3px 8px; border-radius: 6px; background: ${GOLD}; color: #1a1408; }
  .op { text-align: center; font: 800 26px/1 ${DISPLAY}; color: ${FADED}; }
  .money b { display: block; font: 800 ${(p) => (p.$dense ? 24 : 28)}px/1 ${DISPLAY}; font-variant-numeric: tabular-nums; }
  .money small { display: block; margin-top: 3px; font-size: ${(p) => (p.$dense ? 11 : 12.5)}px; line-height: 1.3; color: #b9c1e2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .money.none b { color: ${FADED}; }
  .money.rec b { color: ${GOLD}; }
  .money.one b { color: ${ROSE}; }
  .money.sv b { color: ${BLUE}; }
  .total { text-align: right; }
  .total b { display: block; font: 800 ${(p) => (p.$dense ? 28 : 32)}px/1 ${DISPLAY}; font-variant-numeric: tabular-nums; color: ${(p) => (p.$lead ? GOLD : '#f3f5fb')}; }
  .total small { display: block; margin-top: 3px; font-size: 12.5px; line-height: 1.3; font-weight: 700; color: ${GOLD}; white-space: nowrap; }

  @media (prefers-reduced-motion: reduce) { animation: none; &::after { animation: none; display: none; } }
`;

const Card = styled.section`
  position: absolute;
  top: 664px;
  padding: 12px 18px 10px;
  border-radius: 14px;
  background: rgba(13, 18, 36, 0.55);
  border: 1px solid rgba(170, 181, 221, 0.18);
  line-height: 1.55;

  h2 { margin: 0; font-size: 15px; font-weight: 800; letter-spacing: 0.2em; text-transform: uppercase; color: ${DIM}; }
  .foot { margin-top: 4px; font-size: 12px; color: ${DIM}; }
  .line { display: grid; align-items: center; column-gap: 12px; padding: 2px 0; font-size: 13.5px; font-variant-numeric: tabular-nums; border-top: 1px solid rgba(170, 181, 221, 0.12); }
  .line.cols { border-top: 0; margin-top: 6px; font-size: 11.5px; font-weight: 800; letter-spacing: 0.14em; text-transform: uppercase; color: ${DIM}; }
  .r { text-align: right; }
  .faded { color: ${FADED}; }
  .ell { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`;

/* [ROW-SCORECARD 2026-09-30, user] "show all the sales … latest on the top … it will have a scroll … it will
   auto scroll slowly … if someone wants to see the data then he will use the cursor … whenever there is a
   ten second of pause on that table then it will start automatically again". Second voice in white.
   Since [ROW-LIST-HOVER] a mouse cursor on the card stops it and leaving restarts it at once (the 10 s is left
   for touch and keys), and [ROW-LIST-TEXT] took the status line and the refunds note away. */
const LatestCard = styled(Card)`
  left: 56px;
  width: 960px;

  .sv { color: #ffffff; }
  .line { grid-template-columns: 62px 150px 210px 92px 86px 86px minmax(0, 1fr); }
  .line.cols { padding-right: 12px; }
  .scroller {
    height: 134px;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding-right: 6px;
    scrollbar-width: thin;
    scrollbar-color: rgba(170, 181, 221, 0.4) transparent;
  }
  .scroller::-webkit-scrollbar { width: 6px; }
  .scroller::-webkit-scrollbar-thumb { background: rgba(170, 181, 221, 0.4); border-radius: 3px; }
  .scroller::-webkit-scrollbar-track { background: transparent; }
  .kind { justify-self: start; font-size: 11.5px; font-weight: 800; letter-spacing: 0.08em; padding: 2px 7px; border-radius: 6px; text-transform: uppercase; }
  .kind.rec { background: rgba(244, 184, 96, 0.18); color: ${GOLD}; }
  .kind.one { background: rgba(239, 143, 115, 0.18); color: ${ROSE}; }
`;

const MonthsCard = styled(Card)`
  left: 1040px;
  width: 504px;

  .line { grid-template-columns: minmax(0, 1fr) repeat(var(--months, 4), 70px); }
`;

/* [ROW-ROTATE] The page turns: running by themselves ('auto', next turn at nextAt) or paused by activity
   ('manual', back to auto at idleAt). Kept in a ref — nothing on screen shows it since [ROW-NO-PILL]. */
interface RotState { mode: 'auto' | 'manual'; page: 'summit' | 'table'; nextAt: number; idleAt: number }

const flagIcon = (
  <svg width="22" height="26" viewBox="0 0 22 26" aria-hidden="true">
    <path d="M3 1v24" stroke={SNOW} strokeWidth="2.5" strokeLinecap="round" />
    <path className="pennant" d="M4 2h15l-4 5 4 5H4z" fill={GOLD} />
  </svg>
);

/* [ROW-SCORECARD] The Scorecard's hills (the preview's two ridges), carried on past both stage edges. */
const HILLS_FAR = '-1600,150 -1600,60 -1440,40 -1280,70 -1120,36 -960,66 -800,42 -640,72 -480,38 -320,68 -160,44 0,70 140,40 260,72 400,30 560,66 700,38 860,74 1000,44 1160,70 1300,36 1460,64 1600,46 1760,72 1920,40 2080,68 2240,34 2400,66 2560,42 2720,72 2880,38 3040,64 3200,48 3200,150';
const HILLS_NEAR = '-1600,150 -1600,108 -1400,120 -1200,98 -1000,118 -800,102 -600,121 -400,100 -200,119 0,112 160,96 320,118 480,100 640,122 820,104 1000,120 1200,102 1400,121 1600,106 1800,120 2000,99 2200,118 2400,103 2600,121 2800,100 3000,117 3200,108 3200,150';

/** A sale's second voice as the list shows it: "Self closed" · "Hamza · split 50/50" · "Hamza & Bhanu · 50% shared". */
const voiceText = (voices: string[]) =>
  !voices.length ? 'Self closed' : voices.length === 1 ? `${firstName(voices[0])} · split 50/50` : `${firsts(voices)} · 50% shared`;

/** One money cell of the Scorecard: the amount (a faded dash when there is none) over where it came from. */
const moneyCell = (value: number, cls: 'rec' | 'one' | 'sv', sign: string, caption: string) => (
  <span className={`money ${value ? cls : 'none'}`}>
    <b>{value ? sign + money(value) : '—'}</b>
    <small>{caption}</small>
  </span>
);

/* ───────────── [PHONE-LAYOUT 2026-10-24, user] the phone board ───────────── */
/* Phones (768 px or less) only: the 1600 x 900 TV stage scaled to a phone's width left every word 2–4 px tall
   ("The Summit" and "The Scorecard" alike), so a phone gets the same board as ONE readable page that scrolls: the
   head and countdown, the two seats, the rope-team standings, how every score is made, the latest counted sales
   and the score by month — every figure the TV shows, from the same /api/sales-row/board answer, in the board's own
   colours. Still portaled over the whole window (z-index 950, above the dock), Back / Esc as on the TV. No
   animation and no page turns here. The TV / laptop board is untouched. */
const PhoneScreen = styled.div`
  position: fixed;
  inset: 0;
  z-index: 950;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;
  padding: calc(12px + env(safe-area-inset-top, 0px)) 12px calc(28px + env(safe-area-inset-bottom, 0px));
  background: linear-gradient(180deg, #111934 0%, #1d2550 45%, #27305f 100%);
  color: #f3f5fb;
  font-family: 'Plus Jakarta Sans', system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif;
  display: flex;
  flex-direction: column;
  gap: 12px;

  /* [PHONE-LAYOUT 2026-10-26, user] A fixed-height column that scrolls lets its blocks shrink to fit the screen: the
     36 px Back pill (the only block with a set height) was squashed to 20 px. Every block keeps its own height; the
     screen scrolls instead. */
  & > * { flex-shrink: 0; }
`;

const PhoneBack = styled.button`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 16px;
  border-radius: 999px;
  border: 1px solid rgba(170, 181, 221, 0.35);
  background: rgba(13, 18, 36, 0.62);
  color: #f3f5fb;
  font: 700 14px 'Plus Jakarta Sans', system-ui, sans-serif;
  cursor: pointer;
`;

const PhoneHead = styled.header`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;

  .eyebrow { font-size: 11px; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase; color: ${DIM}; }
  h1 { margin: 2px 0 0; font: 900 46px/0.95 ${DISPLAY}; letter-spacing: 0.01em; text-transform: uppercase; }
  .dates { margin-top: 6px; font-size: 13px; color: ${SOFT}; }
  .count { display: flex; align-items: baseline; gap: 8px; }
  .count b { font: 900 44px/1 ${DISPLAY}; color: ${GOLD}; }
  .count span { font-size: 11px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: ${SOFT}; line-height: 1.25; }
`;

const PhoneSeats = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
`;

const PhoneSeat = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 8px 10px;
  border-radius: 10px;
  background: rgba(13, 18, 36, 0.62);
  border: 1px solid rgba(244, 184, 96, 0.55);

  svg { flex: none; }
  small { display: block; font-size: 11px; font-weight: 800; letter-spacing: 0.14em; color: ${GOLD}; text-transform: uppercase; }
  strong { display: block; font-size: 14px; font-weight: 700; overflow-wrap: anywhere; }
`;

const PhoneCard = styled.section`
  display: grid;
  gap: 10px;
  min-width: 0;
  padding: 12px;
  border-radius: 14px;
  background: rgba(13, 18, 36, 0.55);
  border: 1px solid rgba(170, 181, 221, 0.18);

  h2 { margin: 0; font-size: 12px; font-weight: 800; letter-spacing: 0.16em; text-transform: uppercase; color: ${DIM}; }
  .note { margin: 0; font-size: 12px; color: ${SOFT}; line-height: 1.5; }
`;

const PhoneRow = styled.div<{ $lead: boolean }>`
  display: grid;
  grid-template-columns: 30px minmax(0, 1fr) auto;
  column-gap: 10px;
  row-gap: 6px;
  align-items: center;
  padding: 10px 12px;
  border-radius: 12px;
  background: ${(p) => (p.$lead ? 'rgba(40, 32, 30, 0.55)' : 'rgba(13, 18, 36, 0.55)')};
  border: 1px solid ${(p) => (p.$lead ? 'rgba(244, 184, 96, 0.6)' : 'rgba(170, 181, 221, 0.18)')};

  .rank { grid-row: 1 / 3; font: 900 32px/1 ${DISPLAY}; color: ${(p) => (p.$lead ? GOLD : DIM)}; }
  .name { font-size: 15px; font-weight: 700; display: flex; flex-wrap: wrap; align-items: center; gap: 6px; min-width: 0; overflow-wrap: anywhere; }
  .name i { font-style: normal; font-size: 11px; font-weight: 800; letter-spacing: 0.12em; padding: 2px 7px; border-radius: 6px; background: ${GOLD}; color: #1a1408; }
  .score { grid-row: 1 / 3; grid-column: 3; text-align: right; }
  .score b { display: block; font: 800 26px/1 ${DISPLAY}; font-variant-numeric: tabular-nums; }
  .score small { display: block; margin-top: 3px; font-size: 11px; font-weight: 700; color: ${GOLD}; white-space: nowrap; }
  .bar { grid-column: 2; display: flex; height: 6px; border-radius: 3px; background: rgba(170, 181, 221, 0.16); overflow: hidden; }
  .bar i { display: block; height: 100%; }
  .sub { grid-column: 1 / 4; font-size: 12px; color: #b9c1e2; font-variant-numeric: tabular-nums; line-height: 1.45; }

  /* the Scorecard's breakdown of one score (Recurring, One-time, Shared, Earned) */
  dl { grid-column: 1 / 4; margin: 0; display: grid; grid-template-columns: auto minmax(0, 1fr) auto; column-gap: 10px; row-gap: 6px; align-items: baseline; }
  dt { font-size: 12px; font-weight: 700; color: ${SOFT}; white-space: nowrap; }
  dd.cap { margin: 0; font-size: 11.5px; color: ${DIM}; overflow-wrap: anywhere; }
  dd.val { margin: 0; font-size: 13px; font-weight: 800; text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
  dd.val.rec { color: ${GOLD}; }
  dd.val.one { color: ${ROSE}; }
  dd.val.sv { color: ${BLUE}; }
  dd.val.none { color: ${FADED}; }
`;

const PhoneLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  font-size: 12px;
  color: ${SOFT};

  span { display: inline-flex; align-items: center; gap: 6px; }
  b { width: 11px; height: 11px; border-radius: 3px; display: inline-block; }
`;

/* A real table that swipes sideways in its own box — every row shown (the TV's self-scrolling list is a TV thing). */
const PhoneTableBox = styled.div`
  overflow-x: auto;
  overscroll-behavior-x: contain;
  -webkit-overflow-scrolling: touch;
  margin: 0 -12px;
  padding: 0 12px;

  table { border-collapse: collapse; width: max-content; min-width: 100%; font-variant-numeric: tabular-nums; }
  th { padding: 6px 10px 6px 0; font-size: 11px; font-weight: 800; letter-spacing: 0.12em; text-transform: uppercase; color: ${DIM}; text-align: left; white-space: nowrap; }
  td { padding: 8px 10px 8px 0; font-size: 13px; white-space: nowrap; border-top: 1px solid rgba(170, 181, 221, 0.12); }
  .r { text-align: right; }
  .faded { color: ${FADED}; }
  .sv { color: #ffffff; }
  .kind { font-size: 11px; font-weight: 800; letter-spacing: 0.08em; padding: 2px 7px; border-radius: 6px; text-transform: uppercase; }
  .kind.rec { background: rgba(244, 184, 96, 0.18); color: ${GOLD}; }
  .kind.one { background: rgba(239, 143, 115, 0.18); color: ${ROSE}; }
`;

/* ───────────── the page ───────────── */

const SalesRowPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isPhone = useIsMobile(); // [PHONE-LAYOUT 2026-10-24, user] 768 px or less → the phone board (rendered last)
  // Set by the header button (Header.tsx) — the page the person was on when they clicked it.
  const cameFromApp = Boolean((location.state as { from?: string } | null)?.from);

  const goBack = useCallback(() => {
    // Opened from the button → one step back lands exactly where they clicked it (scroll and all).
    // Opened any other way (a typed address, a bookmark) → their normal start page.
    if (cameFromApp) navigate(-1);
    else navigate('/', { replace: true });
  }, [cameFromApp, navigate]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) goBack();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goBack]);

  /* ── [ROW-ROTATE] the two pages take turns ── */
  const rot = useRef<RotState>({ mode: 'auto', page: 'summit', nextAt: 0, idleAt: 0 });
  const [page, setPage] = useState<'summit' | 'table'>('summit');
  // How many times each page has come up: the climb and the Scorecard's rows play again each time.
  const [runs, setRuns] = useState({ summit: 0, table: 0 });
  const show = useCallback((p: 'summit' | 'table') => {
    if (rot.current.page === p) return;
    rot.current.page = p;
    setPage(p);
    setRuns((r) => ({ ...r, [p]: r[p] + 1 }));
  }, []);
  /* Main screen / Table: show that page and pause the turns until 10 s pass with no activity. */
  const pick = useCallback((p: 'summit' | 'table') => {
    show(p);
    rot.current.mode = 'manual';
    rot.current.idleAt = Date.now() + HOLD_MS;
  }, [show]);
  // [FLUID-8 2026-10-12, user] The page buttons switch on the mouse PRESS, like every switch in the app; touch / keyboard click.
  const navPressedRef = useRef(false);
  const navPress = (p: 'summit' | 'table') => ({
    onPointerDown: (e: React.PointerEvent) => { navPressedRef.current = isPlainMousePress(e); if (navPressedRef.current) pick(p); },
    onClick: (e: React.MouseEvent) => { if (clickEndsPress(navPressedRef, e)) return; pick(p); },
  });
  useEffect(() => {
    rot.current.nextAt = Date.now() + PAGE_MS;
    const t = setInterval(() => {
      const r = rot.current;
      const now = Date.now();
      if (r.mode === 'manual' && now >= r.idleAt) {
        r.mode = 'auto';
        r.nextAt = now + PAGE_MS;
      } else if (r.mode === 'auto' && now >= r.nextAt) {
        show(r.page === 'summit' ? 'table' : 'summit');
        r.nextAt = now + PAGE_MS;
      }
    }, 500);
    return () => clearInterval(t);
  }, [show]);
  /* [ROW-IDLE-10S] Any real activity on the board (a mouse that actually moves, a click, a key, the wheel, a
     touch) pauses the turns — also while they run by themselves — and 10 s without any starts them again. A
     move counts only when the pointer changed position: browsers also send pointer events to a cursor that is
     resting still, and those must not keep the board paused for ever. */
  useEffect(() => {
    let last: [number, number] | null = null;
    const active = () => {
      const r = rot.current;
      r.mode = 'manual';
      r.idleAt = Math.max(r.idleAt, Date.now() + HOLD_MS);
    };
    const onMove = (e: PointerEvent) => {
      if (last && Math.abs(e.clientX - last[0]) + Math.abs(e.clientY - last[1]) < 2) return;
      last = [e.clientX, e.clientY];
      active();
    };
    const others = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;
    window.addEventListener('pointermove', onMove, { passive: true });
    others.forEach((ev) => window.addEventListener(ev, active, { passive: true }));
    return () => {
      window.removeEventListener('pointermove', onMove);
      others.forEach((ev) => window.removeEventListener(ev, active));
    };
  }, []);

  /* ── data: the board, kept live ── */
  const [board, setBoard] = useState<Board | null>(null);
  const [failed, setFailed] = useState(false);
  const load = useCallback(async () => {
    try {
      setBoard(await getJson<Board>('/api/sales-row/board'));
      setFailed(false);
    } catch {
      setFailed(true); // keep the last good standings on screen
    }
  }, []);
  useEffect(() => {
    void load();
    const t = setInterval(() => { if (!document.hidden) void load(); }, 60_000);
    return () => clearInterval(t);
  }, [load]);
  useRealtime(['payments'], () => { void load(); }); // a mapped sale shows within seconds

  /* ── fit the 1600x900 stage into the window ── */
  const [win, setWin] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  useEffect(() => {
    const onResize = () => setWin({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const k = Math.min(win.w / STAGE_W, win.h / STAGE_H);
  /* [ROW-4K 2026-10-01] The corner buttons are in window pixels, so on a big screen (a 4K TV at 100 %) they
     stayed 30 px while the board grew 2.4x. Past a 1080p-sized board (k 1.2) they grow with it, keeping the
     same share of the screen; up to that — every laptop and 1080p screen — they stay exactly as they are. */
  const navZoom = Math.round(Math.max(1, k / 1.2) * 100) / 100;

  /* [ROW-LIVE-FX] Only the stars inside the visible sky are drawn: the stage plus whatever side band a
     wide window shows (in stage pixels). Positions come from the fixed STAR_FIELD, so they never move. */
  const bleedX = k > 0 ? Math.max(0, (win.w / k - STAGE_W) / 2) : 0;
  const stars = useMemo(
    () => STAR_FIELD.filter((s) => s.x >= -bleedX - 10 && s.x <= STAGE_W + bleedX + 10),
    [bleedX],
  );
  /* [ROW-LIVE-FX] CSS cannot stop SVG's own animations (the lantern), so pause them here for anyone
     who asked their system for reduced motion — [ROW-ROTATE] and while The Summit is not on screen. */
  const landRef = useRef<SVGSVGElement>(null);
  useEffect(() => {
    const svg = landRef.current;
    if (!svg) return;
    if (page === 'summit' && !reduceMotion()) svg.unpauseAnimations?.();
    else svg.pauseAnimations?.();
  }, [page]);

  /* ── the climb: markers move along the route to their score ── */
  const reps = useMemo(() => board?.reps ?? [], [board]);
  const top = useMemo(() => {
    const lead = reps.length ? reps[0].score : 0;
    return Math.max(500, Math.ceil((lead * 1.1) / 500) * 500);
  }, [reps]);
  const targetKey = reps.map((r) => `${r.name}:${Math.min(1, r.score / top).toFixed(5)}`).join('|');
  const [shown, setShown] = useState<Record<string, number>>({});
  const shownRef = useRef(shown);
  shownRef.current = shown;
  const climbRun = useRef(runs.summit);
  useEffect(() => {
    if (!targetKey) return;
    const target: Record<string, number> = {};
    const order: string[] = [];
    for (const part of targetKey.split('|')) {
      const i = part.lastIndexOf(':');
      target[part.slice(0, i)] = Number(part.slice(i + 1));
      order.push(part.slice(0, i));
    }
    // [ROW-ROTATE] each time The Summit comes back up, the climb starts again from base camp;
    // a new sale while it is up moves the markers on from where they stand.
    const replay = climbRun.current !== runs.summit;
    climbRun.current = runs.summit;
    const from = replay ? {} : { ...shownRef.current };
    if (reduceMotion()) { setShown(target); return; }
    const D = 1700, STAGGER = 120;
    const t0 = performance.now();
    let raf = 0;
    const step = (now: number) => {
      const next: Record<string, number> = {};
      let done = true;
      order.forEach((name, i) => {
        const p = Math.min(1, Math.max(0, (now - t0 - i * STAGGER) / D));
        if (p < 1) done = false;
        const e = 1 - Math.pow(1 - p, 3);
        const a = from[name] ?? 0;
        next[name] = a + (target[name] - a) * e;
      });
      setShown(next);
      if (!done) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [targetKey, runs.summit]);

  /* Marker positions, nudged sideways when two would sit on top of each other. */
  const markers = useMemo(() => {
    const placed: { name: string; x: number; y: number; lead: boolean; score: number }[] = [];
    reps.forEach((r, i) => {
      let [x, y] = onRoute(shown[r.name] ?? 0);
      for (let n = 0; n < 4 && placed.some((p) => Math.hypot(p.x - x, p.y - y) < 46); n++) x += 38;
      placed.push({ name: r.name, x, y, lead: i < 2 && r.score > 0, score: r.score });
    });
    return placed;
  }, [reps, shown]);

  /* ── [ROW-SCORECARD] "Latest counted sales" scrolls by itself ── */
  const hasBoard = !!board;
  const listCardRef = useRef<HTMLElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const listUsedUntil = useRef(0);
  const listHovered = useRef(false);
  /* [ROW-LIST-HOVER 2026-09-30] While a mouse cursor is anywhere on the card the list stands still and goes
     with the person's own scrolling; the moment the cursor leaves, it glides on by itself. Hover comes from
     pointerenter / pointerleave only — never from mouse moves, which browsers also send to a resting cursor
     while rows scroll under it. Touch and keys have no cursor that can leave, so they stop the list until
     10 s pass untouched. (The page's own pause comes from the board-wide activity listener above.) */
  useEffect(() => {
    const card = listCardRef.current;
    const sc = listRef.current;
    if (!card || !sc) return;
    const enter = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') listHovered.current = true;
    };
    const leave = (e: PointerEvent) => {
      if (e.pointerType === 'touch') return;
      listHovered.current = false;
      listUsedUntil.current = 0; // "start its auto mode again immediately"
    };
    const used = () => { listUsedUntil.current = Date.now() + LIST_IDLE_MS; };
    card.addEventListener('pointerenter', enter);
    card.addEventListener('pointerleave', leave);
    sc.addEventListener('touchstart', used, { passive: true });
    sc.addEventListener('keydown', used);
    return () => {
      card.removeEventListener('pointerenter', enter);
      card.removeEventListener('pointerleave', leave);
      sc.removeEventListener('touchstart', used);
      sc.removeEventListener('keydown', used);
    };
  }, [hasBoard]);
  /* The glide: about one row every two seconds down to the end, a rest, a smooth trip back to the top, a
     rest, and again. It starts from the top each time the Scorecard comes up and runs only while it is on
     screen; with reduced motion the list stays still and people scroll it themselves.
     [ROW-LIST-TEXT 2026-09-30, user] No status line beside the title any more — the user removed both
     "Stopped · scrolls by itself again in 7s" ("will not show on the table") and "Scrolling by itself · move
     the mouse here to stop it". */
  const onTable = page === 'table';
  useEffect(() => {
    const card = listCardRef.current;
    const sc = listRef.current;
    if (!onTable || !card || !sc) return;
    sc.scrollTop = 0;
    if (reduceMotion()) return;
    let state: 'rest' | 'down' | 'up' = 'rest';
    let until = performance.now() + 1500;
    let pos = 0;
    let last = 0;
    let raf = 0;
    const step = (now: number) => {
      const dt = last ? Math.min(0.1, (now - last) / 1000) : 0;
      last = now;
      // A leave can go unseen when the page is hidden under the cursor, so the card must really be hovered too.
      const hovered = listHovered.current && card.matches(':hover');
      if (hovered || listUsedUntil.current > Date.now()) {
        // Stopped: follow the person's own scrolling, and go on from there the moment it is let go.
        pos = sc.scrollTop;
        state = 'rest';
        until = now;
      } else {
        const max = sc.scrollHeight - sc.clientHeight;
        if (max > 0) {
          if (state === 'rest' && now >= until) state = pos >= max - 1 ? 'up' : 'down';
          if (state === 'down') {
            pos = Math.min(max, pos + LIST_SPEED * dt);
            sc.scrollTop = pos;
            if (pos >= max) { state = 'rest'; until = now + 2500; }
          } else if (state === 'up') {
            sc.scrollTo({ top: 0, behavior: 'smooth' });
            pos = 0;
            state = 'rest';
            until = now + 2200;
          }
        }
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [onTable, runs.table, hasBoard]);

  const seat = (i: number) => (reps[i] && reps[i].score > 0 ? reps[i].name : 'Open seat');
  const seat2 = reps[1]?.score ?? 0;
  const dense = reps.length > 5;
  const pct = (v: number) => `${((v / top) * 100).toFixed(2)}%`;
  const end = board?.end ?? '2026-12-31';
  const start = board?.start ?? '2026-10-01'; // [ROW-START-OCT 2026-10-06, user] fallback only — the server sends the real window
  const range = `${dayLabel(start, false)} → ${dayLabel(end, true)}`;
  const months = useMemo(() => monthsBetween(start, end), [start, end]);
  const sales = board?.sales ?? [];
  /* A second voice earns half of a sale's value, or an equal part of that half when a sale had two or more. */
  const earnedWord = (name: string) =>
    sales.some((s) => s.secondVoices.length > 1 && s.secondVoices.some((v) => v.trim().toLowerCase() === name.toLowerCase()))
      ? 'a share of' : 'half of';
  const stageStyle = { transform: `translate(-50%, -50%) scale(${k})` };
  /* Both pages carry the same header and countdown, so a page change reads as one board. */
  const heading = (title: string, sub: string) => (
    <>
      <Head>
        <Eyebrow>Team ROW Sales · Leadership Challenge</Eyebrow>
        <Title>{title}</Title>
        <Dates>{sub}</Dates>
      </Head>
      <Countdown>
        <b>{daysLeft(end)}</b>
        <span>days left<br />to the summit</span>
      </Countdown>
    </>
  );

  /* [PHONE-LAYOUT 2026-10-24, user] Phones: the same board as one readable page (see PhoneScreen). Every hook has
     already run above, so this early return is safe; the TV board below is untouched. */
  if (isPhone) {
    return createPortal(
      <PhoneScreen>
        <PhoneBack type="button" onClick={goBack}>
          <i className="fas fa-arrow-left" aria-hidden="true" />
          Back
        </PhoneBack>

        <PhoneHead>
          <div>
            <div className="eyebrow">Team ROW Sales · Leadership Challenge</div>
            <h1>The Summit</h1>
            <div className="dates">{range} · two leader seats at the top</div>
          </div>
          <div className="count">
            <b>{daysLeft(end)}</b>
            <span>days left<br />to the summit</span>
          </div>
        </PhoneHead>

        <PhoneSeats>
          {[0, 1].map((i) => (
            <PhoneSeat key={i}>
              {flagIcon}
              <div>
                <small>Seat {i + 1}</small>
                <strong>{board ? seat(i) : '…'}</strong>
              </div>
            </PhoneSeat>
          ))}
        </PhoneSeats>

        <PhoneCard>
          <h2>Rope team standings</h2>
          {!board ? (
            <p className="note">{failed ? 'Could not load the standings. Trying again every minute.' : 'Loading the standings…'}</p>
          ) : (
            reps.map((r, i) => {
              const lead = i < 2 && r.score > 0;
              const gap = i >= 2 && seat2 > r.score ? seat2 - r.score : 0;
              return (
                <PhoneRow key={r.name} $lead={lead}>
                  <span className="rank">{i + 1}</span>
                  <span className="name">{r.name}{lead && <i>Seat {i + 1}</i>}</span>
                  <span className="score">
                    <b>{money(r.score)}</b>
                    {gap > 0 && <small>{money(gap)} to Seat 2</small>}
                  </span>
                  <span className="bar" aria-hidden="true">
                    <i style={{ width: pct(r.recurring), background: GOLD }} />
                    <i style={{ width: pct(r.oneTime), background: ROSE }} />
                    <i style={{ width: pct(r.secondVoice), background: BLUE }} />
                  </span>
                  <span className="sub">
                    Recurring {money(r.recurring)} · One-time {money(r.oneTime)} · Second voice {money(r.secondVoice)}
                  </span>
                </PhoneRow>
              );
            })
          )}
          {board && (
            <PhoneLegend>
              <span><b style={{ background: GOLD }} />Recurring</span>
              <span><b style={{ background: ROSE }} />One-time (25%)</span>
              <span><b style={{ background: BLUE }} />Second voice</span>
            </PhoneLegend>
          )}
          {board && failed && <p className="note">Could not refresh just now — showing the last standings.</p>}
        </PhoneCard>

        {board && (
          <PhoneCard>
            <h2>How the score is made</h2>
            <p className="note">
              Recurring + One-time ×25% − shared with a 2nd voice + earned as a 2nd voice = Score. A second voice takes
              half of that sale's value (shared equally when there are two or more).
            </p>
            {reps.map((r, i) => {
              const lead = i < 2 && r.score > 0;
              const gap = i >= 2 && seat2 > r.score ? seat2 - r.score : 0;
              return (
                <PhoneRow key={r.name} $lead={lead}>
                  <span className="rank">{i + 1}</span>
                  <span className="name">{r.name}{lead && <i>Seat {i + 1}</i>}</span>
                  <span className="score">
                    <b>{money(r.score)}</b>
                    {gap > 0 && <small>{money(gap)} to Seat 2</small>}
                  </span>
                  <dl>
                    <dt>Recurring</dt>
                    <dd className="cap">{r.recurringSales ? `${plural(r.recurringSales, 'sale')} · counts 100%` : 'no recurring sales'}</dd>
                    <dd className={`val ${r.ownRecurring ? 'rec' : 'none'}`}>{r.ownRecurring ? money(r.ownRecurring) : '—'}</dd>
                    <dt>+ One-time</dt>
                    <dd className="cap">{r.oneTimeSales ? `${money(r.oneTimeSold)} sold · counts 25%` : 'no one-time sales'}</dd>
                    <dd className={`val ${r.oneTimeValue ? 'one' : 'none'}`}>{r.oneTimeValue ? money(r.oneTimeValue) : '—'}</dd>
                    <dt>− Shared</dt>
                    <dd className="cap">{r.sharedSales ? `half of ${plural(r.sharedSales, 'sale')} · to ${firsts(r.sharedTo)}` : 'nothing shared'}</dd>
                    <dd className={`val ${r.sharedOut ? 'sv' : 'none'}`}>{r.sharedOut ? '−' + money(r.sharedOut) : '—'}</dd>
                    <dt>+ Earned</dt>
                    <dd className="cap">{r.earnedSales ? `${earnedWord(r.name)} ${plural(r.earnedSales, 'sale')} · from ${firsts(r.earnedFrom)}` : 'none yet'}</dd>
                    <dd className={`val ${r.secondVoice ? 'sv' : 'none'}`}>{r.secondVoice ? '+' + money(r.secondVoice) : '—'}</dd>
                  </dl>
                </PhoneRow>
              );
            })}
          </PhoneCard>
        )}

        {board && (
          <PhoneCard>
            <h2>Latest counted sales</h2>
            <PhoneTableBox>
              <table>
                <thead>
                  <tr>
                    <th>Date</th><th>Rep</th><th>Website</th><th>Type</th>
                    <th className="r">Sale</th><th className="r">Counts</th><th>Second voice</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.length ? sales.map((s, i) => (
                    <tr key={`${i}|${s.date}|${s.owner}|${s.website}|${s.kind}`}>
                      <td>{shortDay(s.date)}</td>
                      <td>{s.owner}</td>
                      <td>{s.website || '—'}</td>
                      <td><span className={`kind ${s.kind === 'recurring' ? 'rec' : 'one'}`}>{s.kind === 'recurring' ? 'Recurring' : 'One-time'}</span></td>
                      <td className="r">{money(s.saleAmount)}</td>
                      <td className="r">{money(s.value)}</td>
                      <td className="sv">{voiceText(s.secondVoices)}</td>
                    </tr>
                  )) : (
                    <tr><td className="faded" colSpan={7}>No counted sales yet.</td></tr>
                  )}
                </tbody>
              </table>
            </PhoneTableBox>
            <p className="note">{plural(sales.length, 'sale')} so far, newest first</p>
          </PhoneCard>
        )}

        {board && (
          <PhoneCard>
            <h2>Score by month</h2>
            <PhoneTableBox>
              <table>
                <thead>
                  <tr>
                    <th>Rep</th>
                    {months.map((m) => <th className="r" key={m}>{monthShort(m)}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {reps.map((r) => (
                    <tr key={r.name}>
                      <td>{r.name}</td>
                      {months.map((m) => {
                        const v = r.months[m] || 0;
                        return <td key={m} className={v ? 'r' : 'r faded'}>{v ? money(v, 0) : '—'}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </PhoneTableBox>
            <p className="note">{failed ? 'Could not refresh just now — showing the last scores.' : 'Scores by the month the payment arrived'}</p>
          </PhoneCard>
        )}
      </PhoneScreen>,
      document.body,
    );
  }

  return createPortal(
    <Screen>
      <GroundBand style={{ height: Math.max(0, (win.h - STAGE_H * k) / 2 + 30 * k) }} />
      {/* The stars are shared by both pages, so the sky twinkles on through a page change. */}
      <Stage style={stageStyle}>
        <Stars aria-hidden="true">
          {stars.map((s, i) => (
            <i key={i} style={{ left: s.x, top: s.y, width: s.size, height: s.size, animationDuration: `${s.dur.toFixed(2)}s`, animationDelay: `-${s.delay.toFixed(2)}s` }} />
          ))}
          <div className="shoot" />
        </Stars>
      </Stage>

      {/* ── page 1 · The Summit ── */}
      <Layer $on={page === 'summit'} inert={page !== 'summit'}>
        <Stage style={stageStyle}>
          <Rays aria-hidden="true" />
          <Sun />
          <Land ref={landRef} viewBox={`${-LAND_BLEED} 0 ${STAGE_W + 2 * LAND_BLEED} ${STAGE_H - LAND_TOP}`} aria-hidden="true">
            <defs>
              <linearGradient id="rowFar" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#56629a" /><stop offset="1" stopColor="#343f70" /></linearGradient>
              <linearGradient id="rowMid" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#3b477a" /><stop offset="1" stopColor="#1c2446" /></linearGradient>
              <linearGradient id="rowNear" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#1b2344" /><stop offset="1" stopColor={GROUND} /></linearGradient>
              {/* [ROW-LIVE-FX] soft mist puffs and the lantern's warm glow */}
              <radialGradient id="rowMistG">
                <stop offset="0" stopColor="#e3e9ff" stopOpacity="0.5" />
                <stop offset="0.55" stopColor="#e3e9ff" stopOpacity="0.18" />
                <stop offset="1" stopColor="#e3e9ff" stopOpacity="0" />
              </radialGradient>
              <radialGradient id="rowLanternG">
                <stop offset="0" stopColor="#fff6dc" />
                <stop offset="0.3" stopColor="#ffd88a" stopOpacity="0.95" />
                <stop offset="1" stopColor={GOLD} stopOpacity="0" />
              </radialGradient>
            </defs>
            {/* The far ridge carries on to the LEFT past the stage edge (the side band on a wide window). */}
            <polygon fill="url(#rowFar)" points="-1600,720 -1600,430 -1460,370 -1320,410 -1180,310 -1040,360 -900,290 -760,340 -620,260 -480,330 -340,280 -200,330 -90,300 0,360 120,300 210,340 300,230 390,300 470,260 760,140 840,210 920,170 1000,230 1000,720" />
            {/* [ROW-LIVE-FX] 1 · mist drifting in the valley BEHIND the peak */}
            {mistBand(400, 5, 230, 42, 0.55, 140)}
            <polygon fill="url(#rowMid)" points="40,720 180,560 250,585 360,420 430,450 520,250 600,70 680,220 760,190 860,360 1000,330 1000,720" />
            <polygon fill="#ffffff" opacity="0.06" points="600,70 680,220 760,190 860,360 1000,330 1000,720 640,720" />
            <polygon fill={SNOW} points="600,70 572,132 590,124 603,140 618,120 636,138" />
            {/* The foothills run the full stage width AND past both edges, so the stage's bottom edge is one
                colour and the bands below or beside it on any window shape simply continue the ground. */}
            <polygon fill="url(#rowNear)" points="-1600,720 -1600,660 -1400,640 -1200,668 -1000,645 -800,672 -600,650 -400,668 -200,640 0,655 140,630 300,670 470,640 640,675 820,645 1000,670 1200,650 1400,672 1600,655 1800,640 2000,668 2200,648 2400,670 2600,652 2800,668 3000,650 3200,662 3200,720" />
            <polyline fill="none" stroke={SNOW} strokeOpacity="0.6" strokeWidth="3" strokeDasharray="2 9" strokeLinecap="round"
              points={ROUTE.map((p) => p.join(',')).join(' ')} />
            {/* [ROW-LIVE-FX] 5 · the climber's lantern — the light and its trail, each a little later on the same path */}
            <g>
              {LANTERN_PARTS.map((l, i) => (
                <circle key={i} r={l.r} fill={l.fill} opacity={0}>
                  <animateMotion dur="14s" begin={`${l.begin}s`} repeatCount="indefinite" path={LANTERN_PATH} />
                  <animate attributeName="opacity" values={`0;${l.peak};${l.peak};0;0`} keyTimes="0;0.05;0.86;0.93;1"
                    dur="14s" begin={`${l.begin}s`} repeatCount="indefinite" />
                </circle>
              ))}
            </g>
            {/* [ROW-LIVE-FX] 1 · a lower, faster band of mist IN FRONT of the foothills */}
            {mistBand(660, 4, 300, 34, 0.45, 95)}
            <g transform="translate(150,676)">
              <path d="M0 18 L13 0 L26 18 Z" fill={GOLD} />
              <path d="M13 3 L13 18" stroke={GROUND} strokeWidth="2" />
            </g>
            <text x="112" y="712" fill={SOFT} fontFamily="Plus Jakarta Sans, sans-serif" fontSize="14" fontWeight="700" letterSpacing="2">BASE CAMP · $0</text>
          </Land>

          {heading('The Summit', `${range} · two leader seats at the top`)}

          <Seats>
            {[0, 1].map((i) => (
              <Seat key={i}>
                {flagIcon}
                <div>
                  <small>Seat {i + 1}</small>
                  <strong>{board ? seat(i) : '…'}</strong>
                </div>
              </Seat>
            ))}
          </Seats>

          <div aria-hidden="true">
            {markers.map((m) => (
              <Marker key={m.name} $lead={m.lead} style={{ transform: `translate(${m.x.toFixed(1)}px, ${m.y.toFixed(1)}px)` }}>
                <span className="dot">{initials(m.name)}</span>
                <span className="tag">{firstName(m.name)}<em>{money(m.score, 0)}</em></span>
              </Marker>
            ))}
          </div>

          <Panel>
            <h2>Rope team standings</h2>
            {!board ? (
              <Note>{failed ? 'Could not load the standings. Trying again every minute.' : 'Loading the standings…'}</Note>
            ) : (
              <List $dense={dense}>
                {reps.map((r, i) => {
                  const lead = i < 2 && r.score > 0;
                  const gap = i >= 2 && seat2 > r.score ? seat2 - r.score : 0;
                  return (
                    <Row key={r.name} $lead={lead} $dense={dense}>
                      <span className="rank">{i + 1}</span>
                      <span className="name"><span>{r.name}</span>{lead && <i>Seat {i + 1}</i>}</span>
                      <span className="score">
                        <b>{money(r.score)}</b>
                        {gap > 0 && <small>{money(gap)} to Seat 2</small>}
                      </span>
                      <span className="bar" aria-hidden="true">
                        <i style={{ width: pct(r.recurring), background: GOLD }} />
                        <i style={{ width: pct(r.oneTime), background: ROSE }} />
                        <i style={{ width: pct(r.secondVoice), background: BLUE }} />
                      </span>
                      <span className="sub">
                        Recurring {money(r.recurring)} · One-time {money(r.oneTime)} · Second voice {money(r.secondVoice)}
                      </span>
                    </Row>
                  );
                })}
              </List>
            )}
            {board && (
              <Legend>
                <span><b style={{ background: GOLD }} />Recurring</span>
                <span><b style={{ background: ROSE }} />One-time (25%)</span>
                <span><b style={{ background: BLUE }} />Second voice</span>
              </Legend>
            )}
            {board && failed && <Legend>Could not refresh just now — showing the last standings.</Legend>}
          </Panel>
        </Stage>
      </Layer>

      {/* ── page 2 · [ROW-SCORECARD] The Scorecard ── */}
      <Layer $on={page === 'table'} inert={page !== 'table'}>
        <Stage style={stageStyle}>
          <Hills viewBox={`${-LAND_BLEED} 0 ${STAGE_W + 2 * LAND_BLEED} 150`} aria-hidden="true">
            <defs>
              <linearGradient id="rowScFar" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#4a5687" /><stop offset="1" stopColor="#2d3762" /></linearGradient>
              <linearGradient id="rowScNear" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#1b2344" /><stop offset="1" stopColor={GROUND} /></linearGradient>
              <radialGradient id="rowScMist">
                <stop offset="0" stopColor="#e3e9ff" stopOpacity="0.45" />
                <stop offset="1" stopColor="#e3e9ff" stopOpacity="0" />
              </radialGradient>
            </defs>
            <polygon fill="url(#rowScFar)" points={HILLS_FAR} />
            {mistBand(92, 4, 280, 26, 0.4, 110, 'rowScMist')}
            <polygon fill="url(#rowScNear)" points={HILLS_NEAR} />
          </Hills>

          {heading('The Scorecard', `How every score is made · ${range}`)}

          <Formula>
            <h2>How the score is made</h2>
            <div className="sum">
              <span>Recurring</span><i>+</i><span>One-time ×25%</span><i>−</i><span>shared with a 2nd voice</span>
              <i>+</i><span>earned as a 2nd voice</span><i>=</i><span>Score</span>
            </div>
            <span>A second voice takes half of that sale's value (shared equally when there are two or more).</span>
          </Formula>

          <ScTable $dense={dense}>
            {!board ? (
              <Note>{failed ? 'Could not load the scorecard. Trying again every minute.' : 'Loading the scorecard…'}</Note>
            ) : (
              <>
                <ScHead>
                  <span>#</span>
                  <span>Sales rep</span>
                  <span><b style={{ background: GOLD }} />Recurring</span><span />
                  <span><b style={{ background: ROSE }} />One-time (25%)</span><span />
                  <span><b style={{ background: BLUE }} />Shared</span><span />
                  <span><b style={{ background: BLUE }} />Earned</span><span />
                  <span className="right">Score</span>
                </ScHead>
                {/* keyed by the page's turn, so the rows slide in again each time the Scorecard comes up */}
                <div className="rows" key={runs.table}>
                  {reps.map((r, i) => {
                    const lead = i < 2 && r.score > 0;
                    const gap = i >= 2 && seat2 > r.score ? seat2 - r.score : 0;
                    return (
                      <ScRow key={r.name} $lead={lead} $i={i} $dense={dense}>
                        <span className="rank">{i + 1}</span>
                        <span className="rep"><span>{r.name}</span>{lead && <i>Seat {i + 1}</i>}</span>
                        {moneyCell(r.ownRecurring, 'rec', '',
                          r.recurringSales ? `${plural(r.recurringSales, 'sale')} · counts 100%` : 'no recurring sales')}
                        <span className="op">+</span>
                        {moneyCell(r.oneTimeValue, 'one', '',
                          r.oneTimeSales ? `${money(r.oneTimeSold)} sold · counts 25%` : 'no one-time sales')}
                        <span className="op">−</span>
                        {moneyCell(r.sharedOut, 'sv', '−',
                          r.sharedSales ? `half of ${plural(r.sharedSales, 'sale')} · to ${firsts(r.sharedTo)}` : 'nothing shared')}
                        <span className="op">+</span>
                        {moneyCell(r.secondVoice, 'sv', '+',
                          r.earnedSales ? `${earnedWord(r.name)} ${plural(r.earnedSales, 'sale')} · from ${firsts(r.earnedFrom)}` : 'none yet')}
                        <span className="op">=</span>
                        <span className="total">
                          <b>{money(r.score)}</b>
                          {lead ? <small>Seat {i + 1}</small> : gap > 0 ? <small>{money(gap)} to Seat 2</small> : null}
                        </span>
                      </ScRow>
                    );
                  })}
                </div>
              </>
            )}
          </ScTable>

          {board && (
            <LatestCard ref={listCardRef}>
              <h2>Latest counted sales</h2>
              <div className="line cols">
                <span>Date</span><span>Rep</span><span>Website</span><span>Type</span>
                <span className="r">Sale</span><span className="r">Counts</span><span>Second voice</span>
              </div>
              <div className="scroller" ref={listRef} tabIndex={0} aria-label="All counted sales, newest first">
                {sales.length ? sales.map((s, i) => (
                  <div className="line" key={`${i}|${s.date}|${s.owner}|${s.website}|${s.kind}`}>
                    <span>{shortDay(s.date)}</span>
                    <span className="ell">{s.owner}</span>
                    <span className="ell">{s.website || '—'}</span>
                    <span className={`kind ${s.kind === 'recurring' ? 'rec' : 'one'}`}>{s.kind === 'recurring' ? 'Recurring' : 'One-time'}</span>
                    <span className="r">{money(s.saleAmount)}</span>
                    <span className="r">{money(s.value)}</span>
                    <span className="ell sv">{voiceText(s.secondVoices)}</span>
                  </div>
                )) : <div className="line faded">No counted sales yet.</div>}
              </div>
              {/* [ROW-LIST-TEXT 2026-09-30, user] "remove this line …": the refunds / disputes / PIA note is gone */}
              <div className="foot">{plural(sales.length, 'sale')} so far, newest first</div>
            </LatestCard>
          )}

          {board && (
            <MonthsCard style={{ ['--months' as any]: months.length }}>
              <h2>Score by month</h2>
              <div className="line cols">
                <span>Rep</span>
                {months.map((m) => <span className="r" key={m}>{monthShort(m)}</span>)}
              </div>
              {reps.map((r) => (
                <div className="line" key={r.name}>
                  <span className="ell">{r.name}</span>
                  {months.map((m) => {
                    const v = r.months[m] || 0;
                    return <span key={m} className={v ? 'r' : 'r faded'}>{v ? money(v, 0) : '—'}</span>;
                  })}
                </div>
              ))}
              <div className="foot">
                {failed ? 'Could not refresh just now — showing the last scores.' : 'Scores by the month the payment arrived'}
              </div>
            </MonthsCard>
          )}
        </Stage>
      </Layer>

      <Nav aria-label="Board pages" style={navZoom > 1 ? { zoom: navZoom } : undefined}>
        <NavButton type="button" onClick={goBack} title="Back (Esc)">
          <i className="fas fa-arrow-left" aria-hidden="true" />
          Back
        </NavButton>
        <NavButton type="button" $active={page === 'summit'} aria-pressed={page === 'summit'} {...navPress('summit')}>
          <i className="fas fa-mountain-sun" aria-hidden="true" />
          Main screen
        </NavButton>
        <NavButton type="button" $active={page === 'table'} aria-pressed={page === 'table'} {...navPress('table')}>
          <i className="fas fa-table-list" aria-hidden="true" />
          Table
        </NavButton>
      </Nav>
    </Screen>,
    document.body,
  );
};

export default SalesRowPage;
